<template>
  <div id="whale-screen-results">
    <v-card v-if="!results">
      <v-card-text class="text-body-2"> Screen not run yet. </v-card-text>
    </v-card>

    <v-container v-else id="#results-header" fluid pa-0>
      <!-- Buttons: Excel, Start/Stop tracking -->
      <v-row>
        <v-col cols="12">
          <v-btn
            :href="`/generated_backtesters/${results.excel_file}`"
            :small="$vuetify.display.xs"
            target="_blank"
            color="primary"
            class="ml-0"
          >
            Download Screen Results to Excel
          </v-btn>

          <v-dialog v-model="CreateWatchlistDialog" width="500">
            <template #activator="{ props }">
              <v-btn
                :small="$vuetify.display.xs"
                target="_blank"
                v-bind="props"
                color="secondary"
                class="ml-0"
              >
                Create a Watchlist From Results
              </v-btn>
            </template>

            <v-card>
              <v-card-title class="text-h5 bg-grey-lighten-2">
                Create Stock Watchlist
              </v-card-title>

              <v-card-text>
                <v-alert density="compact" type="info">
                  The number of stocks that will be added to the watchlist will be limited by your
                  subscription status. Your current limit is {{ limit }} per watchlist
                </v-alert>

                <v-alert v-if="todaysIdeasItems.length == 0" density="compact" type="warning">
                  No stocks were matched
                </v-alert>
                <v-text-field
                  v-model="newWatchlist.description"
                  hide-details
                  clearable
                  autofocus
                  autocorrect="off"
                  spellcheck="false"
                  class="d-inline-flex pt-0"
                  style="min-width: 300px"
                  @keypress.enter="addWatchlist()"
                />
              </v-card-text>

              <v-divider></v-divider>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" variant="outlined" @click="CreateWatchlistDialog = false">
                  Cancel
                </v-btn>
                <v-btn
                  color="primary"
                  variant="outlined"
                  :disabled="
                    newWatchlist.description.trim().length == 0 || todaysIdeasItems.length == 0
                  "
                  @click="addWatchlist()"
                >
                  Create Watchlist
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>

      <!-- Ending Portfolio -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-text class="pa-6 bg-grey-darken-3 text-white">
              <h2 class="text-h5 mb-2 d-inline-flex align-center">
                <img
                  src="https://d27mjrcvcy56qq.cloudfront.net/images/ico_graph.png"
                  class="mr-2"
                />
                Todays Trading Ideas
              </h2>
              <div>
                Click the "Download Screen Results to Excel" button above to see all stock activity
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Stocks Table & Ending Sector Allocation chart -->
      <v-row mb-2>
        <v-col cols="12" xl="6">
          <v-card>
            <v-card-text>
              <v-data-table
                id="todays-ideas-table"
                :headers="todaysIdeasHeaders"
                :items="todaysIdeasItems"
                hide-default-footer
                items-per-page="-1"
              >
                <template #item="{ item }">
                  <!-- Default row -->
                  <tr v-if="!item.rowType">
                    <td>
                      <a
                        :href="`/stock/${item.stock_permalink}`"
                        :title="item.stock_name"
                        class="font-weight-bold"
                        target="_blank"
                        v-text="item.symbol"
                      />
                      {{ item.stock_name }}
                      <span v-if="item.is_short"> (short) </span>
                    </td>
                    <td>
                      {{ item.sector }}
                    </td>
                    <td>
                      {{ item.transaction_date }}
                    </td>
                    <td>
                      {{ item.description }}
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Ending Sector Allocation chart -->
        <v-col cols="12" xl="6">
          <v-card>
            <v-card-text>
              <div id="backtest_sector_chart" />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Ending Market Cap chart -->
        <v-col cols="12" xl="6">
          <v-card>
            <v-card-text>
              <div id="backtest_mc_chart" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import Highcharts from 'highcharts'
import api from '@/store/api'
import last from 'lodash/last' // returns the last element of a given array
import { mapStoreState } from '@/store/helpers'
import { useGoTo } from 'vuetify'
// Mixins
import FormattersMixin from '@/mixins/formatters'

export default {
  mixins: [FormattersMixin],

  setup() {
    const goTo = useGoTo()
    return { goTo }
  },

  data: () => ({
    results: null,
    criteria: {},
    lowest_y: 0,
    highest_y: 0,
    CreateWatchlistDialog: false,
    newWatchlist: { id: null, description: 'New Watchlist', visibility_type_id: 0, stocks: [] },
    chartData: [],
    indices: [],
    maToAdd: null,
    moving_averages: [],
    starting_record: null,
    hedged_matrix: null,
    ending_hedged_transaction: null,

    stocksTableHeaders: [
      { title: 'Stock Name', key: 'Stock Name', sortable: false },
      { title: '% of Portfolio', key: '% of Portfolio', sortable: false },
      { title: 'Total Return', key: 'Total Return', sortable: false },
      { title: 'Original Purchase Date', key: 'Original Purchase Date', sortable: false }
    ],
    todaysIdeasHeaders: [
      { title: 'Stock', key: 'Stock Name', sortable: false },
      { title: 'Sector', key: 'sector', sortable: false },
      { title: 'Date', key: '% of Portfolio', sortable: false },
      { title: 'Source', key: 'Total Return', sortable: false }
    ]
  }),

  computed: {
    ...mapStoreState({
      limit: state => state.user.limit
    }),
    stocksTableItems() {
      return [
        ...this.results.portfolio.holdings,
        { rowType: 'cashBalance' }
        //  { rowType: 'marginBalance' }
      ]
    },
    todaysIdeasItems() {
      return [...this.results.todays_trading_ideas]
    }
  },

  methods: {
    clearResults() {
      this.results = null
    },
    genResults(results, criteria) {
      this.results = results
      this.criteria = criteria
      this.indices = [] // this.results.performance_matrices.slice(1) // slice removes 'Whales' entry

      this.$nextTick(() => {
        this.loadSectorChart('backtest_sector_chart', this.results.todays_trading_ideas)

        this.loadMcChart('backtest_mc_chart', this.results.todays_trading_ideas)
      })

      this.$nextTick(function () {
        setTimeout(
          () =>
            this.goTo('#results-header', {
              offset: 20,
              duration: 2000
            }),
          100
        )
      })
    },

    loadSectorChart(element, data) {
      var chartSeries = []
      var series = {
        name: 'Sectors',
        data: []
      }

      var totalMV = data.length
      /* if (this.results.portfolio.margin_reserve){
        h["marginReserve"]={
          name: 'Margin Reserve',
          y: Math.round(parseFloat(this.results.portfolio.margin_reserve) * 100) / 100
        }
      } */

      var h = {}
      for (var i = 0; i < data.length; i++) {
        var stock = data[i]

        if (!h[stock.sector]) {
          h[stock.sector] = {
            name: stock.sector,
            y: 0
          }
        }
        h[stock.sector].y += 1
      }

      for (var propertyName in h) {
        if (propertyName === undefined) continue
        var entry = h[propertyName]
        if (!entry.y) continue
        entry.y = (entry.y / totalMV) * 100.0
        series.data.push(entry)
      }

      chartSeries.push(series)

      Highcharts.chart(element, {
        chart: {
          type: 'pie',
          backgroundColor: '#FFFFFF'
        },
        title: {
          text: 'Sector Allocation'
        },
        xAxis: {
          type: 'category'
        },
        yAxis: {
          min: 0,
          title: {
            text: 'Sector Percentage'
          }
        },
        tooltip: {
          pointFormat:
            '<span style="color:{series.color}">{series.name}</span>:  ({point.percentage:.0f}%)<br/>'
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: false
            },
            showInLegend: true
          }
        },
        credits: {
          enabled: false
        },
        series: chartSeries
      })
    },
    get_market_cap_description(mc_id) {
      var description = ''
      var marketCaps = [
        { name: 'Over $200 billion', id: 1 },
        { name: '$100 billion - $200 billion', id: 2 },
        { name: '$50 billion - $100 billion', id: 3 },
        { name: '$20 billion - $50 billion', id: 4 },
        { name: '$10 billion - $20 billion', id: 5 },
        { name: '$2 billion - $10 billion', id: 6 },
        { name: '$1 billion - $2 billion', id: 7 },
        { name: '$500 million - $1 billion', id: 8 },
        { name: '$250 million - $500 million', id: 9 },
        { name: 'Below $250 million', id: 10 }
      ]
      for (var i = 0; i < marketCaps.length; i++) {
        if (marketCaps[i].id == mc_id) {
          description = marketCaps[i].name
          break
        }
      }
      return description
    },
    loadMcChart(element, data) {
      var chartdata = []
      var h = {}
      for (var i = 0; i < data.length; i++) {
        var stock = data[i]

        if (!stock.market_cap_id) continue
        if (!h[stock.market_cap_id]) {
          h[stock.market_cap_id] = {
            name: this.get_market_cap_description(stock.market_cap_id),
            y: 0
          }
        }
        h[stock.market_cap_id].y += 1
      }

      for (var propertyName in h) {
        if (propertyName === undefined) continue
        var entry = h[propertyName]
        if (!entry.y) continue
        chartdata.push(entry)
      }

      Highcharts.chart(element, {
        chart: {
          plotBackgroundColor: null,
          plotBorderWidth: null,
          plotShadow: false,
          type: 'pie'
        },
        credits: {
          enabled: false
        },
        title: {
          text: 'Concentration by Stock Market Cap'
        },
        tooltip: {
          pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>: {point.percentage:.1f} %',
              style: {
                color: (Highcharts.theme && Highcharts.theme.contrastTextColor) || 'black'
              }
            }
          }
        },
        series: [
          {
            name: '% Concentration',
            colorByPoint: true,
            data: chartdata
          }
        ]
      })
    },
    addWatchlist() {
      //verify name and at least one stock
      if (this.newWatchlist.description.trim().length == 0 || this.todaysIdeasItems.length == 0)
        return
      this.saving = true
      for (var i = 0; i < this.todaysIdeasItems.length; i++) {
        if (this.newWatchlist.stocks.length >= this.limit) {
          break
        }
        this.newWatchlist.stocks.push({ id: this.todaysIdeasItems[i].stock_id })
      }
      let url = 'watchlists'
      api
        .post(url, this.newWatchlist)
        .then(response => {
          useUserStore().loadWatchlists()
          useAppStore().showNotification({ type: 'success', text: 'Watchlist has been saved' })
          this.newWatchlist = {
            id: null,
            description: 'New Watchlist',
            visibility_type_id: 0,
            stocks: []
          }
          this.CreateWatchlistDialog = false
        })
        .catch(error => {
          useAppStore().showNotification({ type: 'error', text: error.message })
        })
        .then(() => (this.saving = false))
    }
  }
}
</script>

<style lang="scss">
#backtest-table,
#backtest-stocks-table {
  thead th {
    color: rgba(0, 0, 0, 0.87);
  }
}
</style>
